import type { AppPlugin } from '@capacitor/app';
import type { BrowserPlugin } from '@capacitor/browser';
import type { PreferencesPlugin } from '@capacitor/preferences';
import type { PushNotificationsPlugin } from '@capacitor/push-notifications';
import { createClient } from '../api/client';
import { LINK_HOSTS, apiBaseFor, issuerFor, siteOriginFor, type PlanletsEnv } from '../auth/config';
import type { PendingLogin } from '../auth/flow';
import { clearSession, getAccessToken, type Session } from '../auth/session';
import { newId } from './ids';
import type { Platform, PlatformEvents, PushChannel, ReturnTo, UnsupportedReason } from './platform';

export type NativeKind = 'ios' | 'android';

/** The slices of the Capacitor plugins the adapter uses — the real ones are wired in index.ts. */
export type AppLike = Pick<AppPlugin, 'addListener' | 'getLaunchUrl' | 'exitApp' | 'getInfo'>;
export type BrowserLike = Pick<BrowserPlugin, 'open' | 'close'>;
export type PreferencesLike = Pick<PreferencesPlugin, 'get' | 'set' | 'remove'>;
/** The slice of the push plugin the adapter uses (ADR-030 §2): permission, registration, the events. */
export type PushLike = Pick<
  PushNotificationsPlugin,
  'checkPermissions' | 'requestPermissions' | 'register' | 'unregister' | 'addListener'
>;

/** The in-repo secure-storage plugin (Keychain / EncryptedSharedPreferences — ADR-029 §4). */
export interface SecureStoreLike {
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
}

/**
 * The in-repo iOS sign-in user agent (ADR-029 §4 — `ASWebAuthenticationSession`, which reuses
 * Safari's IdP session): resolves with the callback URL, rejects with code `CANCELLED` when the
 * user dismissed it, and — where the shell does not implement it — with Capacitor's
 * `UNIMPLEMENTED`, which sends the adapter to the external browser instead.
 */
export interface AuthSessionLike {
  start(options: { url: string; callbackScheme: string }): Promise<{ url: string }>;
}

/** What the WebView offers — the ADR-029 §2 boot assertions. */
export interface WebViewCapabilities {
  readonly secureContext: boolean;
  readonly subtleCrypto: boolean;
  readonly randomUUID: boolean;
}

export interface NativeDeps {
  readonly kind: NativeKind;
  readonly env: PlanletsEnv;
  readonly app: AppLike;
  readonly browser: BrowserLike;
  readonly preferences: PreferencesLike;
  readonly secureStore: SecureStoreLike;
  readonly capabilities: WebViewCapabilities;
  /** The iOS authentication session; ignored on Android (Custom Tabs share Chrome's cookies). */
  readonly authSession?: AuthSessionLike;
  /** The push plugin (ADR-030 §2); a shell without it has no push channel. */
  readonly push?: PushLike;
  /** The transport — the WebView's `fetch`, which `CapacitorHttp` patches (ADR-029 §4). */
  readonly fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

const PENDING_KEY = 'planlets.pendingLogin';
const RETURN_KEY = 'planlets.returnTo';
const SESSION_KEY = 'planlets.session';
const LANGUAGE_KEY = 'planlets.language';
const PUSH_ID_KEY = 'planlets.pushInstallationId';

/** The private-use scheme of an environment's apps (launch decision 44). */
export function schemeFor(env: PlanletsEnv): string {
  return env === 'prod' ? 'planlets' : 'planlets-dev';
}

/**
 * The native shells' `Platform` (ADR-029 §4/§5): one client id per platform, the environment's
 * private-use scheme as the redirect URI, the absolute API base of the environment's SWA, the
 * in-flight login and the resume target in the app's preferences (the WebView process can die
 * while the external browser is open), the session in the OS secure store for the token's
 * lifetime, the authorize URL opened in the external browser (Custom Tabs on Android, the
 * system browser session on iOS), the OS events — the URL the callback and the https links
 * arrive on (running or at a cold start), foregrounding, the hardware back button — and the
 * push channel (ADR-030 §2): the OS's push service, its token recorded on the API under a
 * per-install id, a tapped notice routed to the plan it names — and the language choice in
 * the app's preferences (ADR-031).
 */
export function createNativePlatform(deps: NativeDeps): Platform {
  const { kind, env, app, browser, preferences, secureStore, capabilities, authSession, push } = deps;
  const scheme = schemeFor(env);
  const transport = deps.fetch ?? ((input, init) => fetch(input, init));
  // The app's event handlers, once it listens — the iOS authentication session hands its
  // callback URL straight back here rather than through the OS.
  let listening: PlatformEvents | null = null;

  const storeSession = async (session: Session | null): Promise<void> => {
    try {
      if (session === null) await secureStore.remove({ key: SESSION_KEY });
      else await secureStore.set({ key: SESSION_KEY, value: JSON.stringify(session) });
    } catch {
      // No secure store in this shell (the plugin is not implemented yet): the session lives in
      // memory only, exactly like the web page's.
    }
  };

  const client = createClient({
    baseUrl: apiBaseFor(env),
    fetch: transport,
    getToken: getAccessToken,
    onUnauthorized: () => {
      clearSession();
      void storeSession(null);
    },
  });

  const take = async <T>(key: string): Promise<T | null> => {
    const { value } = await preferences.get({ key });
    await preferences.remove({ key });
    return value === null ? null : (JSON.parse(value) as T);
  };

  // A link on the app's own scheme is parsed as https first: Chromium before 130 — the API 34
  // emulator's WebView is 113, and the shell admits WebViews from 92 — gives a non-special
  // scheme no host at all (`new URL('planlets-dev://diag').host` is '' and the path is
  // '//diag'), while Node, jsdom and WebKit follow the standard. The device smoke found it
  // (mobile-ci run 24): the link arrived twice and matched nothing.
  const own = `${scheme}://`;
  const route = (raw: string, events: PlatformEvents): void => {
    const ours = raw.startsWith(own);
    let url: URL;
    try {
      url = new URL(ours ? `https://${raw.slice(own.length)}` : raw);
    } catch {
      return;
    }
    if (ours) {
      // The auth callback (RFC 8252): close the external browser and hand the code over.
      if (url.host === 'auth' && url.pathname === '/callback') {
        void browser.close().catch(() => undefined);
        events.onAuthCallback(url.searchParams);
      } else if (url.host === 'diag') {
        // The device smoke's way to the transport probe (ADR-029 §7): the shells claim only
        // /p/* of the https site, so `<scheme>://diag` stands in for `/?diag=1`; the dev
        // flavour of the app is what honours it.
        events.onDeepLink(new URL('/?diag=1', siteOriginFor(env)));
      }
      return;
    }
    if (url.protocol === 'https:' && LINK_HOSTS.includes(url.hostname)) events.onDeepLink(url);
  };

  // The install's push identity (ADR-030 §2): one id per install, minted on first use and kept
  // in the preferences — not a secret, and it must outlive sign-outs so the API sees the same
  // device each time. The API scopes it by the signed-in principal.
  const installationId = async (): Promise<string> => {
    const { value } = await preferences.get({ key: PUSH_ID_KEY });
    if (value !== null) return value;
    const fresh = newId();
    await preferences.set({ key: PUSH_ID_KEY, value: fresh });
    return fresh;
  };

  // The push service answered with a token (on every register(), and when it rotates one):
  // record the device on the API. A failure — offline, the session ended between the two
  // calls — is left to the next enable().
  const recordDevice = async (token: string): Promise<void> => {
    try {
      await client.registerPushDevice(await installationId(), {
        platform: kind === 'ios' ? 'apns' : 'fcm',
        token,
      });
    } catch {
      // Best effort by contract (ADR-030 §2).
    }
  };

  const pushChannel: PushChannel | null =
    push === undefined
      ? null
      : {
          enable: async () => {
            try {
              let status = await push.checkPermissions();
              if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
                status = await push.requestPermissions();
              }
              if (status.receive !== 'granted') return false;
              await push.register();
              return true;
            } catch {
              // No push service in this build (an Android shell without google-services.json,
              // a simulator without APNs): the app simply has no channel.
              return false;
            }
          },
          disable: async () => {
            try {
              const { value } = await preferences.get({ key: PUSH_ID_KEY });
              if (value !== null) await client.unregisterPushDevice(value);
            } catch {
              // The API side is best effort; the OS side below still runs.
            }
            try {
              await push.unregister();
            } catch {
              // Already unregistered, or no push service in this build.
            }
          },
        };

  return {
    kind,
    auth: {
      issuer: issuerFor(env),
      clientId: `planlets-${kind}`,
      redirectUri: `${scheme}://auth/callback`,
    },
    client,
    savePending: async (login) => {
      await preferences.set({ key: PENDING_KEY, value: JSON.stringify(login) });
    },
    takePending: () => take<PendingLogin>(PENDING_KEY),
    openAuth: async (url) => {
      if (kind === 'ios' && authSession !== undefined) {
        try {
          const callback = await authSession.start({ url, callbackScheme: scheme });
          if (listening !== null) route(callback.url, listening);
          return;
        } catch (error) {
          // Cancelled: nothing to do (the pending login is overwritten by the next attempt).
          if ((error as { code?: string }).code !== 'UNIMPLEMENTED') return;
        }
      }
      await browser.open({ url });
    },
    rememberReturnTo: async (target) => {
      await preferences.set({ key: RETURN_KEY, value: JSON.stringify(target) });
    },
    takeReturnTo: () => take<ReturnTo>(RETURN_KEY),
    loadSession: async () => {
      try {
        const { value } = await secureStore.get({ key: SESSION_KEY });
        return value === null ? null : (JSON.parse(value) as Session);
      } catch {
        return null;
      }
    },
    storeSession,
    push: pushChannel,
    loadLanguage: async () => (await preferences.get({ key: LANGUAGE_KEY })).value,
    storeLanguage: async (locale) => {
      await preferences.set({ key: LANGUAGE_KEY, value: locale });
    },
    listen: (events) => {
      listening = events;
      const handles = [
        app.addListener('appUrlOpen', ({ url }) => route(url, events)),
        app.addListener('appStateChange', ({ isActive }) => {
          if (isActive) events.onResume();
        }),
        app.addListener('backButton', () => {
          if (!events.onBack()) void app.exitApp();
        }),
      ];
      if (push !== undefined) {
        handles.push(
          push.addListener('registration', ({ value }) => void recordDevice(value)),
          // A tap on a notice (ADR-030 §3): the plan it names, as the SPA's own deep link.
          push.addListener('pushNotificationActionPerformed', ({ notification }) => {
            const planId = (notification.data as { planId?: unknown } | undefined)?.planId;
            if (typeof planId === 'string' && planId !== '') {
              events.onDeepLink(new URL(`/?plan=${encodeURIComponent(planId)}`, siteOriginFor(env)));
            }
          }),
        );
      }
      // A cold start from the callback or an https link arrives here, not on appUrlOpen.
      void app
        .getLaunchUrl()
        .then((launch) => {
          if (launch?.url) route(launch.url, events);
        })
        .catch(() => undefined);
      return () => {
        listening = null;
        for (const handle of handles) void handle.then((h) => h.remove()).catch(() => undefined);
      };
    },
    unsupported: (): UnsupportedReason | null =>
      !capabilities.secureContext
        ? 'insecure-context'
        : !capabilities.subtleCrypto
          ? 'no-webcrypto'
          : !capabilities.randomUUID
            ? 'no-random-uuid'
            : null,
    describeInstall: async () => {
      try {
        const info = await app.getInfo();
        return `${kind} ${info.version} (${info.build})`;
      } catch {
        return null;
      }
    },
  };
}
